import { useEffect, useRef, useState, FormEvent } from 'react';

// Número WhatsApp no formato internacional (sem + ou espaços)
const WA_NUMBER = '5541992570505';

// Link direto para WhatsApp (botão lateral)
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Olá, gostaria de falar com a KPR Advogados.')}`;

// Títulos exatos dos cards da seção "Áreas de Atuação"
const AREAS_DE_INTERESSE = [
  'Direito Societário',
  'Contratos Empresariais e Bancários',
  'Recuperação Judicial',
  'Conformidade (Compliance)',
  'Setor de Óleo & Gás',
  'Agronegócio',
  'Propriedade Intelectual',
  'Participações e Estrutura Patrimonial',
  'Análise Jurídica de Imóveis',
  'Assessoria Completa em Leilões',
  'Regularização Pós-arrematação',
  'Estratégias para Investidores',
];

export default function Contact() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Ao submeter: monta mensagem e abre WhatsApp em nova aba
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    // Impede comportamento padrão do formulário (sem reload, sem POST)
    e.preventDefault();

    const form = e.currentTarget;
    const data = new FormData(form);

    const nome = (data.get('nome') as string).trim();
    const email = (data.get('email') as string).trim();
    const area = (data.get('area') as string).trim();
    const mensagem = (data.get('mensagem') as string).trim();

    // Valida limite de caracteres da mensagem
    if (mensagem.length > 500) return;

    // Monta a mensagem com estrutura exata solicitada
    const texto =
      `Nome Completo: ${nome} | E-mail: ${email} | Área de Interesse: ${area} | Motivo do Contato: ${mensagem}`;

    // Codifica para URL e abre WhatsApp em nova aba
    const url = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank', 'noopener,noreferrer');

    // Feedback visual de envio concluído
    setSent(true);
    form.reset();
    setCharCount(0);
  };

  const inputClass =
    'w-full bg-transparent border border-kpr-light/60 focus:border-kpr-gold outline-none px-4 py-3.5 text-kpr-dark text-sm placeholder:text-kpr-gray/40 transition-colors duration-200 font-light tracking-wide';

  return (
    <section id="contato" className="bg-kpr-cream py-28 lg:py-36" ref={ref}>
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="mb-16">
          <div className={`flex items-center gap-4 mb-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="w-10 h-px bg-kpr-gold" />
            <span className="text-kpr-gold text-xs font-medium tracking-[0.25em] uppercase">Fale Conosco</span>
          </div>
          <h2 className={`font-serif text-4xl md:text-5xl text-kpr-dark font-light leading-[1.1] mb-4 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Entre em contato
          </h2>
          <p className={`text-kpr-gray text-[15px] font-light leading-loose max-w-lg transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Agende uma consulta estratégica ou tire suas dúvidas. Nossa equipe está pronta para atender você.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
          {/* Contact Info */}
          <div className={`lg:col-span-2 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            {/* WA highlight */}
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-5 p-6 bg-kpr-dark group cursor-pointer hover:bg-kpr-medium transition-colors duration-300 mb-8"
            >
              <div className="w-12 h-12 flex items-center justify-center border border-kpr-gold/50 shrink-0">
                <i className="ri-whatsapp-line text-kpr-gold text-xl w-6 h-6 flex items-center justify-center" />
              </div>
              <div>
                <div className="text-white/50 text-xs tracking-wider uppercase mb-0.5">WhatsApp – Atendimento direto</div>
                <div className="text-white font-serif text-xl font-light">(41) 99257-0505</div>
              </div>
              <i className="ri-arrow-right-up-line text-kpr-gold/50 ml-auto w-5 h-5 flex items-center justify-center group-hover:text-kpr-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
            </a>

            {/* Info items */}
            <div className="space-y-6">
              {[
                {
                  icon: 'ri-map-pin-line',
                  label: 'Endereço',
                  content: (
                    <div className="text-kpr-gray text-sm leading-relaxed font-light">
                      R. Padre Agostinho, 2960<br />
                      São Francisco – Curitiba/PR<br />
                      CEP 80710-000
                    </div>
                  ),
                },
                {
                  icon: 'ri-mail-line',
                  label: 'E-mail',
                  content: <div className="text-kpr-gray text-sm font-light">contato@kpradvogados.com.br</div>,
                },
                {
                  icon: 'ri-instagram-line',
                  label: 'Instagram',
                  content: (
                    <a
                      href="https://www.instagram.com/kpradvogados"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-kpr-gray hover:text-kpr-gold text-sm font-light transition-colors cursor-pointer"
                    >
                      @kpradvogados
                    </a>
                  ),
                },
                {
                  icon: 'ri-global-line',
                  label: 'Atuação',
                  content: <div className="text-kpr-gray text-sm font-light">Nacional | Foco em PR e SC</div>,
                },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center border border-kpr-gold/25 shrink-0 mt-0.5">
                    <i className={`${item.icon} text-kpr-gold text-base w-4 h-4 flex items-center justify-center`} />
                  </div>
                  <div>
                    <div className="text-kpr-dark font-medium text-xs tracking-wider uppercase mb-1.5">{item.label}</div>
                    {item.content}
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="mt-8 overflow-hidden h-44 w-full border border-kpr-light">
              <iframe
                title="Localização KPR Advogados – R. Padre Agostinho, 2960, Curitiba/PR"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3603.135789!2d-49.2832!3d-25.4281!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94dce40bef2e2a31%3A0x0!2sR.+Padre+Agostinho%2C+2960+-+S%C3%A3o+Francisco%2C+Curitiba+-+PR%2C+80710-000!5e0!3m2!1spt-BR!2sbr!4v1710000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div className={`lg:col-span-3 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="bg-white p-10 border border-kpr-light">
              <div className="w-10 h-px bg-kpr-gold mb-6" />
              <h3 className="font-serif text-kpr-dark text-2xl font-light mb-8">Envie uma mensagem</h3>

              {sent ? (
                /* Feedback de sucesso */
                <div className="text-center py-16">
                  <div className="w-14 h-14 flex items-center justify-center border border-kpr-gold mx-auto mb-6">
                    <i className="ri-whatsapp-line text-kpr-gold text-2xl w-6 h-6 flex items-center justify-center" />
                  </div>
                  <h4 className="font-serif text-kpr-dark text-xl font-light mb-3">WhatsApp aberto!</h4>
                  <p className="text-kpr-gray text-sm font-light">
                    Sua mensagem foi preparada e o WhatsApp foi aberto em uma nova aba.<br />
                    Basta clicar em "Enviar" no WhatsApp para concluir.
                  </p>
                  <button
                    className="mt-6 text-kpr-gold text-xs tracking-widest uppercase font-medium hover:text-kpr-dark transition-colors cursor-pointer"
                    onClick={() => setSent(false)}
                  >
                    Enviar outra mensagem
                  </button>
                </div>
              ) : (
                <form
                  id="contact-kpr-wa"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  {/* Nome Completo */}
                  <div>
                    <label className="block text-kpr-dark text-xs font-medium tracking-wider uppercase mb-2">
                      Nome Completo <span className="text-kpr-gold">*</span>
                    </label>
                    <input
                      type="text"
                      name="nome"
                      required
                      placeholder="Seu nome completo"
                      className={inputClass}
                    />
                  </div>

                  {/* E-mail */}
                  <div>
                    <label className="block text-kpr-dark text-xs font-medium tracking-wider uppercase mb-2">
                      E-mail <span className="text-kpr-gold">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="seu@email.com"
                      className={inputClass}
                    />
                  </div>

                  {/* Área de Interesse */}
                  <div>
                    <label className="block text-kpr-dark text-xs font-medium tracking-wider uppercase mb-2">
                      Área de Interesse <span className="text-kpr-gold">*</span>
                    </label>
                    <select
                      name="area"
                      required
                      defaultValue=""
                      className={`${inputClass} cursor-pointer appearance-none`}
                      style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'8\' viewBox=\'0 0 12 8\'%3E%3Cpath d=\'M1 1l5 5 5-5\' stroke=\'%23C9A96E\' stroke-width=\'1.5\' fill=\'none\' stroke-linecap=\'round\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                    >
                      <option value="" disabled>Selecione uma área...</option>
                      {AREAS_DE_INTERESSE.map((area) => (
                        <option key={area} value={area}>{area}</option>
                      ))}
                    </select>
                  </div>

                  {/* Mensagem */}
                  <div>
                    <label className="block text-kpr-dark text-xs font-medium tracking-wider uppercase mb-2">
                      Mensagem <span className="text-kpr-gold">*</span>
                    </label>
                    <textarea
                      name="mensagem"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Descreva brevemente sua necessidade jurídica..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className={`${inputClass} resize-none`}
                    />
                    <div className={`text-xs text-right mt-1 font-light ${charCount > 480 ? 'text-red-500' : 'text-kpr-gray/50'}`}>
                      {charCount}/500
                    </div>
                  </div>

                  {/* Botão Enviar */}
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 bg-kpr-dark hover:bg-kpr-gold text-white hover:text-kpr-dark font-semibold text-xs tracking-widest uppercase py-4 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center" />
                    Enviar pelo WhatsApp
                  </button>

                  <p className="text-kpr-gray/50 text-xs text-center font-light leading-relaxed">
                    Ao clicar em enviar, o WhatsApp será aberto com sua mensagem já preenchida.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
